/**
 * US Map Component
 * Displays US states map with task density visualization
 */

import React, { useState } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
} from 'react-simple-maps';
import { Box, Tooltip as MuiTooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

const geoUrl = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';

interface USStateData {
  state: string;
  stateCode: string;
  taskDensity: number;
  tasks: number;
}

interface USMapProps {
  data: USStateData[];
  onStateSelect?: (stateCode: string, stateName: string) => void;
  selectedState?: string;
}

const USMap: React.FC<USMapProps> = ({ data, onStateSelect, selectedState }) => {
  const theme = useTheme();
  const [tooltipContent, setTooltipContent] = useState<{
    state: string;
    density: number;
    tasks: number;
  } | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);

  // Create a map of state codes to data
  const dataMap = new Map(
    data.map((item) => [item.stateCode, item])
  );

  const getFillColor = (density: number) => {
    if (density >= 70) {
      return theme.palette.error.main;
    } else if (density >= 50) {
      return theme.palette.warning.main;
    } else if (density >= 30) {
      return theme.palette.info.main;
    } else {
      return theme.palette.success.main;
    }
  };

  const getOpacity = (density: number) => {
    // Normalize density to 0.3-0.9 opacity range
    return 0.3 + (density / 100) * 0.6;
  };

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      <ComposableMap
        projection="geoAlbersUsa"
        style={{
          width: '100%',
          height: 'auto',
        }}
      >
        <ZoomableGroup>
          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map((geo) => {
                // us-atlas states-10m.json uses 'name' property for state names
                // We need to match by state name or code
                const geoName = geo.properties.name || geo.properties.NAME;
                let stateCode = geo.properties.abbrev || geo.properties.postal || geo.properties.id;
                
                // If we don't have a code, try to find it by matching state name
                if (!stateCode && geoName) {
                  const foundState = Array.from(dataMap.values()).find(
                    (state) => state.state.toLowerCase() === geoName.toLowerCase()
                  );
                  if (foundState) {
                    stateCode = foundState.stateCode;
                  }
                }
                
                // Also try direct match by name
                const stateData = stateCode 
                  ? dataMap.get(stateCode) 
                  : Array.from(dataMap.values()).find(
                      (state) => state.state.toLowerCase() === geoName?.toLowerCase()
                    );
                
                const density = stateData?.taskDensity || 0;
                const fillColor = getFillColor(density);
                const opacity = getOpacity(density);
                
                // Use the actual state code from data if found
                const actualStateCode = stateData?.stateCode || stateCode;

                const isSelected = selectedState === actualStateCode;
                const strokeColor = isSelected ? theme.palette.primary.main : theme.customColors.tableBorder;
                const strokeWidth = isSelected ? 2 : 0.5;

                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={fillColor}
                    fillOpacity={isSelected ? Math.min(1, opacity + 0.3) : opacity}
                    stroke={strokeColor}
                    strokeWidth={strokeWidth}
                    style={{
                      default: {
                        outline: 'none',
                        cursor: stateData ? 'pointer' : 'default',
                        transition: 'all 0.2s ease',
                      },
                      hover: {
                        fillOpacity: Math.min(1, opacity + 0.2),
                        outline: 'none',
                        strokeWidth: 1.5,
                        stroke: theme.palette.primary.main,
                      },
                      pressed: {
                        outline: 'none',
                      },
                    }}
                    onClick={() => {
                      if (stateData && actualStateCode && onStateSelect) {
                        onStateSelect(actualStateCode, stateData.state);
                      }
                    }}
                    onMouseEnter={(event) => {
                      if (stateData) {
                        setTooltipContent({
                          state: stateData.state,
                          density: stateData.taskDensity,
                          tasks: stateData.tasks,
                        });
                        setTooltipPosition({
                          x: event.clientX,
                          y: event.clientY,
                        });
                      }
                    }}
                    onMouseMove={(event) => {
                      if (tooltipContent) {
                        setTooltipPosition({
                          x: event.clientX,
                          y: event.clientY,
                        });
                      }
                    }}
                    onMouseLeave={() => {
                      setTooltipContent(null);
                      setTooltipPosition(null);
                    }}
                  />
                );
              })
            }
          </Geographies>
        </ZoomableGroup>
      </ComposableMap>

      {/* Tooltip */}
      {tooltipContent && tooltipPosition && (
        <MuiTooltip
          open={true}
          title={
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, marginBottom: 0.5 }}>
                {tooltipContent.state}
              </Typography>
              <Typography variant="caption">
                Tasks: {tooltipContent.tasks.toLocaleString()}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block' }}>
                Density: {tooltipContent.density}%
              </Typography>
            </Box>
          }
          placement="top"
          arrow
          PopperProps={{
            style: {
              position: 'fixed',
              top: tooltipPosition.y - 10,
              left: tooltipPosition.x,
              pointerEvents: 'none',
              zIndex: 1000,
            },
          }}
        >
          <Box />
        </MuiTooltip>
      )}

      {/* Legend */}
      <Box
        sx={{
          position: 'absolute',
          bottom: theme.spacing(2),
          left: theme.spacing(2),
          backgroundColor: theme.palette.background.paper,
          padding: theme.spacing(1.5),
          borderRadius: theme.shape.borderRadius,
          border: `1px solid ${theme.customColors.tableBorder}`,
          boxShadow: theme.shadows[2],
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
            marginBottom: theme.spacing(1),
            display: 'block',
            fontSize: theme.typography.caption.fontSize,
          }}
        >
          Task Density
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              backgroundColor: theme.palette.success.main,
              borderRadius: '2px',
            }}
          />
          <Typography variant="caption" sx={{ fontSize: theme.typography.caption.fontSize, color: theme.palette.text.secondary }}>
            Low (0-30%)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1), marginTop: 0.5 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              backgroundColor: theme.palette.info.main,
              borderRadius: '2px',
            }}
          />
          <Typography variant="caption" sx={{ fontSize: theme.typography.caption.fontSize, color: theme.palette.text.secondary }}>
            Medium (30-50%)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1), marginTop: 0.5 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              backgroundColor: theme.palette.warning.main,
              borderRadius: '2px',
            }}
          />
          <Typography variant="caption" sx={{ fontSize: theme.typography.caption.fontSize, color: theme.palette.text.secondary }}>
            High (50-70%)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1), marginTop: 0.5 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              backgroundColor: theme.palette.error.main,
              borderRadius: '2px',
            }}
          />
          <Typography variant="caption" sx={{ fontSize: theme.typography.caption.fontSize, color: theme.palette.text.secondary }}>
            Very High (70%+)
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default USMap;

